import { useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { GenieLoader } from '@/components/ui/GenieLoader';
import { SearchInput } from '@/components/ui/search-input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePortals, useDeletePortal, useReorderPortals } from '@/hooks/portal';
import { useAccountBranding } from '@/hooks/branding/useAccountBranding';
import { Portal, PortalWithCount } from '@/types/portal/portal';
import LearnMoreButton from '@/components/learn/LearnMoreButton';
import PortalCard from './components/PortalCard';
import CreatePortalSheet from './components/CreatePortalSheet';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const PortalsPage = () => {
  const { data: portals = [], isLoading } = usePortals();
  const { branding: accountBranding } = useAccountBranding();
  const { mutate: deletePortal, isPending: isDeleting } = useDeletePortal();
  const { mutate: reorderPortals } = useReorderPortals();

  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [portalToDelete, setPortalToDelete] = useState<PortalWithCount | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const filteredPortals = useMemo(() => {
    if (!searchTerm) return portals;
    const searchLower = searchTerm.toLowerCase();
    return portals.filter(
      (portal) =>
        portal.name.toLowerCase().includes(searchLower) ||
        portal.description?.toLowerCase().includes(searchLower) ||
        portal.slug.toLowerCase().includes(searchLower)
    );
  }, [portals, searchTerm]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = portals.findIndex((p) => p.id === active.id);
    const newIndex = portals.findIndex((p) => p.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(portals, oldIndex, newIndex);
      reorderPortals(newOrder.map((p) => p.id));
    }
  };

  const handleEdit = (portal: PortalWithCount) => {
    // Navigate to detail page for editing
    window.location.href = `/genie-hub/portals/${portal.id}`;
  };

  const handleDeleteConfirm = () => {
    if (!portalToDelete) return;
    deletePortal(portalToDelete.id, {
      onSuccess: () => {
        setPortalToDelete(null);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Portals</h1>
          <GenieLoader size="block" loadingText="Loading your portals…" className="py-16" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1920px]">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-0 mb-6 md:mb-8">
          <div className="flex items-center">
            <GenieIcon name="dashboard" size={28} className="h-7 w-7 md:h-8 md:w-8 mr-3 md:mr-4" />
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-[#222B45] leading-tight">
                Portals
              </h1>
              <p className="text-sm md:text-base text-[#64748B] mt-1 hidden sm:block">
                Create custom pages that showcase multiple genies together
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <LearnMoreButton topic="portals" />
            <Button
              onClick={() => setShowCreateSheet(true)}
              className="flex items-center justify-center px-4 md:px-6 py-2 md:py-3 text-white rounded-xl font-semibold transition-all shadow-lg text-sm md:text-base hover:opacity-90"
              style={{
                background: 'linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)',
                boxShadow:
                  '0 10px 15px -3px rgba(78,156,255,0.3), 0 4px 6px -2px rgba(214,64,159,0.25)',
              }}
            >
              <Plus className="w-4 h-4 md:w-5 md:h-5 mr-2" />
              Create Portal
            </Button>
          </div>
        </div>

        {portals.length > 0 ? (
          <>
            {/* Search */}
            <div className="flex items-center justify-between mb-6">
              <div className="text-lg font-semibold text-gray-800">
                Your Portals
                <span className="ml-2 text-sm text-gray-500">
                  ({filteredPortals.length} portal{filteredPortals.length !== 1 ? 's' : ''})
                </span>
              </div>
              <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                <div className="rounded-[10px] bg-white">
                  <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search portals..."
                    className="w-64"
                    inputClassName="border-transparent focus-visible:ring-0"
                  />
                </div>
              </div>
            </div>

            {/* Portal Grid */}
            {filteredPortals.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredPortals.map((p) => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col gap-4">
                    {filteredPortals.map((portal) => (
                      <PortalCard
                        key={portal.id}
                        portal={portal}
                        genieCount={portal.genie_count}
                        geniesSummary={portal.genies_summary}
                        onEdit={handleEdit}
                        onDelete={setPortalToDelete}
                        isDragEnabled={!searchTerm}
                        accountBranding={accountBranding}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">No portals found matching "{searchTerm}"</p>
                <Button variant="outline" onClick={() => setSearchTerm('')}>
                  Clear search
                </Button>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-[#4E9CFF]/20 to-[#FF4DB8]/20 rounded-full flex items-center justify-center">
              <GenieIcon name="dashboard" size={48} className="w-12 h-12" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">No Portals Yet</h2>
            <p className="text-sm md:text-base text-gray-600 mb-6 md:mb-8 max-w-md mx-auto px-4">
              Portals let you group multiple genies on a single page. Create one to showcase your
              support team, product assistants, or any collection of genies.
            </p>
            <Button
              onClick={() => setShowCreateSheet(true)}
              className="inline-flex items-center px-8 py-4 text-white rounded-xl font-semibold transition-all shadow-lg hover:opacity-90"
              style={{
                background: 'linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)',
              }}
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Portal
            </Button>
          </div>
        )}
      </div>

      {/* Create Portal Sheet */}
      <CreatePortalSheet isOpen={showCreateSheet} onClose={() => setShowCreateSheet(false)} />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!portalToDelete} onOpenChange={() => setPortalToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Portal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{portalToDelete?.name}"? This action cannot be
              undone. The genies themselves will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PortalsPage;
